import React, { useState, useEffect, useRef } from 'react';
import { useWindowDimensions, ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListsManager from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import { useOrg } from '../hooks/useOrg';
import { useTheme } from '../theme/ThemeContext';
import ListDetailPanel from './ListDetailPanel';
import NewListScreen from './NewListScreen';
import { FaSearch, FaPlus, FaClipboard, FaTimes, FaArrowLeft, FaExclamationTriangle, FaCog } from 'react-icons/fa';

let T = {};

function ListItem({ list, isSelected, onSelect, onSettings, isRTL }) {
  const initial = (list.name || '?')[0].toUpperCase();
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`ml-item${isSelected ? ' sel' : ''}`}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 10,
        borderLeft: isRTL ? 'none' : `3px solid ${isSelected ? T.primary : T.border}`,
        borderRight: isRTL ? `3px solid ${isSelected ? T.primary : T.border}` : 'none',
        marginBottom: 10,
        padding: '26px 18px',
        background: isSelected ? 'rgba(77,166,255,0.08)' : T.card,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        position: 'relative',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: 'rgba(77,166,255,0.12)',
        border: '1px solid rgba(77,166,255,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginTop: 0,
      }}>
        <span style={{ color: T.primary, fontSize: 15, fontWeight: 800 }}>{initial}</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color: T.text, fontSize: 13, fontWeight: 700, lineHeight: '18px',
          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          marginBottom: 0,
          ...(isRTL ? { paddingLeft: 24 } : { paddingRight: 24 }),
        }}>
          {list.name}
        </div>
      </div>
      {/* Gear icon */}
      <div
        className="ml-gear"
        onClick={(e) => {
          e.stopPropagation();
          onSettings(list);
        }}
        style={{
          position: 'absolute',
          top: 12,
          ...(isRTL ? { left: 10 } : { right: 10 }),
          width: 24, height: 24, borderRadius: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          opacity: hovered || isSelected ? 1 : 0,
          transition: 'opacity 0.15s, background 0.12s',
          background: hovered ? T.surface : 'transparent',
        }}
      >
        <FaCog size={11} color={T.textMuted} />
      </div>
      {isSelected && (
        <div style={{ width: 6, height: 6, borderRadius: 3, background: T.primary, flexShrink: 0, marginTop: 7, position: 'absolute', ...(isRTL ? { left: 10 } : { right: 10 }), bottom: 12 }} />
      )}
    </div>
  );
}

const MyListsScreen = () => {
  const { t, i18n } = useTranslation();
  const { width } = useWindowDimensions();
  const { theme } = useTheme();
  const { activeOrg } = useOrg();
  const navigate = useNavigate();
  const isMobile = width < 768;

  T = {
    bg:        theme.bg,
    card:      theme.bgCard,
    surface:   theme.bgSurface,
    border:    theme.border,
    text:      theme.textPrimary,
    textSec:   theme.textSecondary,
    textMuted: theme.textMuted,
    primary:   '#4DA6FF',
  };

  const [allLists, setAllLists] = useState([]);
  const [filteredLists, setFilteredLists] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedList, setSelectedList] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showNewList, setShowNewList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarW, setSidebarW] = useState(240);
  const dragRef = useRef(null);
  const dragStartRef = useRef(null);

  useEffect(() => {
    loadLists();
    AnalyticsService.logActivity('view_my_lists');
  }, [activeOrg?.id]); // eslint-disable-line

  const loadLists = async () => {
    setIsLoading(true);
    setError('');
    try {
      const fetchedLists = await ListsManager.fetchLists(activeOrg?.id);
      if (fetchedLists) {
        setAllLists(fetchedLists);
        setFilteredLists(fetchedLists);
      } else {
        setError(t('app.myLists.failedToLoad'));
      }
    } catch (err) {
      console.error('Error loading lists:', err);
      setError(t('app.myLists.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchText(query);
    if (!query.trim()) {
      setFilteredLists(allLists);
      return;
    }
    const filtered = allLists.filter(list =>
      list.name.toLowerCase().includes(query.toLowerCase()) ||
      (list.request && list.request.toLowerCase().includes(query.toLowerCase()))
    );
    setFilteredLists(filtered);
  };

  const handleSelectList = (list) => {
    setShowNewList(false);
    setShowSettings(false);
    setSelectedList({ id: list.id, name: list.name });
    if (isMobile) setShowDetail(true);
  };

  const handleListSettings = (list) => {
    setShowNewList(false);
    setSelectedList({ id: list.id, name: list.name });
    setShowSettings(true);
    if (isMobile) setShowDetail(true);
  };

  const handleListDeleted = (deletedId) => {
    setAllLists(prev => prev.filter(l => l.id !== deletedId));
    setFilteredLists(prev => prev.filter(l => l.id !== deletedId));
    setSelectedList(null);
    setShowSettings(false);
    if (isMobile) setShowDetail(false);
  };

  const handleListUpdated = (updatedList) => {
    loadLists();
    if (selectedList?.id === updatedList.id && updatedList.name) {
      setSelectedList(prev => ({ ...prev, name: updatedList.name }));
    }
  };

  const handleNewList = () => {
    setSelectedList(null);
    setShowSettings(false);
    setShowNewList(true);
    if (isMobile) setShowDetail(true);
  };

  const handleNewListClose = (created) => {
    setShowNewList(false);
    if (created) {
      loadLists();
    }
  };

  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = true;
    dragStartRef.current = { x: e.clientX, sidebarW };
    const onMove = (mv) => {
      const rawDx = mv.clientX - dragStartRef.current.x;
      const dx = i18n.language === 'he' ? -rawDx : rawDx;
      setSidebarW(Math.max(180, Math.min(380, dragStartRef.current.sidebarW + dx)));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const css = `
    .ml-item { transition: background 0.12s; cursor: pointer; }
    .ml-item:hover { background: ${T.surface} !important; }
    .ml-item.sel { background: rgba(77,166,255,0.08) !important; }
    .ml-item:hover .ml-gear { opacity: 1 !important; }
    .ml-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
    .ml-divider:hover, .ml-divider:active { background: ${T.primary}55; }
    .ml-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${T.border}; border-radius: 1px; }
    ::-webkit-scrollbar { width: 4px; height: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.2); border-radius: 2px; }
  `;

  const sidebarJsx = (
    <div style={{
      width: isMobile ? '100%' : sidebarW,
      flexShrink: 0,
      borderRight: isMobile ? 'none' : `1px solid ${T.border}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 14px 10px',
        flexShrink: 0,
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ color: T.text, fontSize: 16, fontWeight: 700, letterSpacing: 0.3, marginBottom: 2 }}>
              {t('app.myLists.myLists')}
            </div>
            <div style={{ color: T.textMuted, fontSize: 10, letterSpacing: 1.2, fontWeight: 600 }}>
              {filteredLists.length} {filteredLists.length !== 1 ? t('app.myLists.lists') : t('app.myLists.list')}
            </div>
          </div>
          <button
            onClick={handleNewList}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 11px', borderRadius: 20,
              background: T.primary, border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(77,166,255,0.3)',
            }}
          >
            <FaPlus size={9} color="#fff" />
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>{t('app.myLists.newList')}</span>
          </button>
        </div>

        {/* Search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: T.surface, borderRadius: 8, padding: '7px 10px',
          border: `1px solid ${T.border}`,
        }}>
          <FaSearch size={10} color={T.textMuted} />
          <input
            value={searchText}
            onChange={e => handleSearch(e.target.value)}
            placeholder={t('app.myLists.searchPlaceholder')}
            style={{
              flex: 1, background: 'transparent',
              border: 'none', outline: 'none',
              color: T.text, fontSize: 12,
            }}
          />
          {searchText && (
            <div onClick={() => handleSearch('')} style={{ cursor: 'pointer' }}>
              <FaTimes size={10} color={T.textMuted} />
            </div>
          )}
        </div>
      </div>

      {/* Scrollable list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 6px 8px' }}>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: 32, gap: 12 }}>
            <ActivityIndicator size="small" color={T.primary} />
            <span style={{ color: T.textMuted, fontSize: 12 }}>{t('app.myLists.loadingLists')}</span>
          </div>
        ) : error ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: 24, gap: 10 }}>
            <FaExclamationTriangle size={20} color="#ff453a" />
            <span style={{ color: T.textSec, fontSize: 12, textAlign: 'center' }}>{error}</span>
            <button
              onClick={loadLists}
              style={{ padding: '6px 14px', borderRadius: 8, background: T.primary,
                border: 'none', cursor: 'pointer', color: '#fff', fontSize: 11, fontWeight: 600 }}
            >
              {t('app.common.retry')}
            </button>
          </div>
        ) : filteredLists.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: 24, gap: 8 }}>
            <FaClipboard size={24} color={T.textMuted} />
            <span style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>
              {searchText ? t('app.myLists.noMatchingLists') : t('app.myLists.noListsYet')}
            </span>
            <span style={{ color: T.textMuted, fontSize: 11, textAlign: 'center' }}>
              {searchText
                ? t('app.myLists.tryDifferentSearch')
                : t('app.myLists.createFirstList')}
            </span>
            {!searchText && (
              <button
                onClick={handleNewList}
                style={{ marginTop: 4, padding: '7px 14px', borderRadius: 20,
                  background: T.primary, border: 'none', cursor: 'pointer',
                  color: '#fff', fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <FaPlus size={9} color="#fff" />
                {t('app.myLists.createList')}
              </button>
            )}
          </div>
        ) : (
          filteredLists.map(list => (
            <ListItem
              key={list.id}
              list={list}
              isSelected={!showNewList && selectedList?.id === list.id}
              onSelect={() => handleSelectList(list)}
              onSettings={() => handleListSettings(list)}
              isRTL={i18n.language === 'he'}
            />
          ))
        )}
      </div>
    </div>
  );

  const renderRightArea = () => {
    if (showNewList) {
      return (
        <NewListScreen
          isForEditing={false}
          onClose={handleNewListClose}
          isModal={true}
        />
      );
    }

    if (selectedList) {
      return (
        <ListDetailPanel
          key={selectedList.id}
          listId={selectedList.id}
          listName={selectedList.name}
          onListDeleted={handleListDeleted}
          onListUpdated={handleListUpdated}
          showSettings={showSettings}
          onCloseSettings={() => setShowSettings(false)}
        />
      );
    }

    return (
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 16,
        background: T.bg,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: 20,
          background: 'rgba(77,166,255,0.08)',
          border: '1px solid rgba(77,166,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <FaClipboard size={28} color={T.textMuted} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: T.text, fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            {t('app.myLists.selectAList')}
          </div>
          <div style={{ color: T.textMuted, fontSize: 13 }}>
            {t('app.myLists.chooseListFromLeft')}
          </div>
        </div>
      </div>
    );
  };

  const detailViewJsx = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {isMobile && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
          background: T.card,
        }}>
          <div
            onClick={() => {
              setShowDetail(false);
              setShowNewList(false);
              setShowSettings(false);
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              cursor: 'pointer', color: T.primary, fontSize: 13, fontWeight: 600,
            }}
          >
            <FaArrowLeft size={12} color={T.primary} />
            {t('app.myLists.myLists')}
          </div>
        </div>
      )}
      {renderRightArea()}
    </div>
  );

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div style={{
        display: 'flex', flexDirection: 'column',
        height: '100vh', background: T.bg, overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {isMobile ? (
            (showDetail || showNewList) ? detailViewJsx : sidebarJsx
          ) : (
            <>
              {sidebarJsx}
              <div className="ml-divider" onMouseDown={startDrag} />
              {renderRightArea()}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MyListsScreen;
